import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Users, Building2, Activity, BarChart3, TrendingUp,
  Globe, Loader2, Clock, Shield, Wifi, WifiOff,
  ListChecks, Receipt, FileText, CalendarDays, UserCheck,
  AlertTriangle, RefreshCcw, ChevronRight, MapPin,
  HardDrive, Database, Image, FolderOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// ── Mini Bar Chart (CSS-only) ──
const MiniBarChart = ({ data, dataKey = 'count', labelKey = 'label', color = '#3b82f6', height = 120 }) => {
  const max = Math.max(...data.map(d => d[dataKey]), 1);
  return (
    <div className="flex items-end gap-[3px] h-full" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
          <div
            className="w-full rounded-t transition-all hover:opacity-80 min-h-[2px]"
            style={{ height: `${(d[dataKey] / max) * 100}%`, backgroundColor: color }}
          />
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {d[labelKey] || d.date?.slice(5)}: {d[dataKey]}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Stat Card ──
const StatCard = ({ label, value, icon: Icon, color, subtext, trend }) => (
  <Card className="overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          <TrendingUp className={`h-3.5 w-3.5 ${trend >= 0 ? 'text-green-500' : 'text-red-500 rotate-180'}`} />
          <span className={`text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-xs text-gray-400">vs last month</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// ── Progress Bar ──
const ProgressBar = ({ label, value, max, color = 'bg-blue-500' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 truncate">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const PlatformAnalytics = () => {
  const [dashData, setDashData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const { getAuthHeaders } = useAuth();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const officeParam = selectedOffice !== 'all' ? `?office_id=${selectedOffice}` : '';
      const [dashRes, usageRes, storageRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/super-admin-dashboard${officeParam}`, { headers }),
        fetch(`${API_URL}/api/analytics/platform-usage`, { headers }),
        fetch(`${API_URL}/api/analytics/storage`, { headers })
      ]);
      if (dashRes.ok) setDashData(await dashRes.json());
      if (usageRes.ok) setUsageData(await usageRes.json());
      if (storageRes.ok) setStorageData(await storageRes.json());
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [selectedOffice]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F2E6E]" />
      </div>
    );
  }

  const d = dashData || {};
  const u = usageData || {};
  const login = u.login_activity || {};
  const active = u.active_users || {};
  const platform = u.platform_stats || {};
  const tasks = d.tasks || {};
  const expenses = d.expenses || {};
  const attendance = d.attendance || {};
  const demographics = d.demographics || {};
  const tenure = d.tenure || {};
  const depts = d.departments || {};
  const offBreakdown = d.offices_breakdown || {};

  return (
    <div className="space-y-6" data-testid="platform-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Analytics</h1>
          <p className="text-gray-500 text-sm">
            Real-time monitoring and insights across all offices
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedOffice} onValueChange={setSelectedOffice}>
            <SelectTrigger className="w-[200px]" data-testid="analytics-office-filter">
              <SelectValue placeholder="All Offices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Offices</SelectItem>
              {(d.offices || []).map(o => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="analytics-refresh">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Live Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Online Now" value={active.online_now || 0} icon={Wifi} color="bg-green-100 text-green-600" subtext="Active in last hour" />
        <StatCard label="Active (24h)" value={active.active_24h || 0} icon={Activity} color="bg-blue-100 text-blue-600" subtext="Sessions today" />
        <StatCard label="Logins Today" value={login.logins_today || 0} icon={Shield} color="bg-purple-100 text-purple-600" subtext={`${login.failed_logins_today || 0} failed`} />
        <StatCard label="Total Users" value={active.total_users || 0} icon={Users} color="bg-indigo-100 text-indigo-600" subtext={`${active.inactive_users || 0} inactive`} />
        <StatCard label="Offices" value={platform.total_offices || d.total_offices || 0} icon={Building2} color="bg-amber-100 text-amber-600" subtext={`${platform.inactive_offices || 0} inactive`} />
        <StatCard label="Employees" value={d.total_employees || 0} icon={UserCheck} color="bg-teal-100 text-teal-600" subtext={`${tenure.new_hires_this_month || 0} new this month`} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">System Usage</TabsTrigger>
          <TabsTrigger value="storage" data-testid="tab-storage">Storage</TabsTrigger>
          <TabsTrigger value="workforce" data-testid="tab-workforce">Workforce</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
        </TabsList>

        {/* ── OVERVIEW TAB ── */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Platform Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              { label: 'Tasks', value: platform.total_tasks, icon: ListChecks, color: 'text-blue-600' },
              { label: 'Expenses', value: platform.total_expenses, icon: Receipt, color: 'text-green-600' },
              { label: 'Leave Req.', value: platform.total_leave_requests, icon: CalendarDays, color: 'text-orange-600' },
              { label: 'Letters', value: platform.total_letters, icon: FileText, color: 'text-purple-600' },
              { label: 'Contracts', value: platform.total_contracts, icon: FileText, color: 'text-indigo-600' },
              { label: 'Meetings', value: platform.total_meetings, icon: Users, color: 'text-teal-600' },
              { label: 'Audit Logs', value: platform.total_audit_logs, icon: Activity, color: 'text-gray-600' },
              { label: 'Overdue Tasks', value: tasks.overdue_tasks, icon: AlertTriangle, color: 'text-red-600' },
            ].map(({ label, value, icon: Icon, color }) => (
              <Card key={label}>
                <CardContent className="p-3 text-center">
                  <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
                  <p className="text-lg font-bold">{value || 0}</p>
                  <p className="text-[10px] text-gray-500">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Employees by Office + Recent Logins */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Employees by Office</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(offBreakdown.by_office || {}).map(([name, count]) => (
                    <ProgressBar key={name} label={name} value={count} max={d.total_employees || 1} color="bg-[#0F2E6E]" />
                  ))}
                  {Object.keys(offBreakdown.by_office || {}).length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Recent Logins</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {(u.recent_logins || []).map((log, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs bg-[#0F2E6E]/10 text-[#0F2E6E]">{getInitials(log.user_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{log.user_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-400">
                          {log.created_at?.slice(0, 16).replace('T', ' ')}
                          {log.location && <span className="ml-2">{log.location}</span>}
                        </p>
                      </div>
                      {log.ip_address && (
                        <span className="text-[10px] text-gray-400 font-mono">{log.ip_address}</span>
                      )}
                    </div>
                  ))}
                  {(!u.recent_logins || u.recent_logins.length === 0) && (
                    <p className="text-gray-400 text-sm text-center py-4">No recent logins</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── SYSTEM USAGE TAB ── */}
        <TabsContent value="usage" className="mt-6 space-y-6">
          {/* Daily Logins Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Daily Logins (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[160px]">
                <MiniBarChart data={login.daily_logins || []} color="#0F2E6E" height={140} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
                <span>{(login.daily_logins || [])[0]?.date?.slice(5) || ''}</span>
                <span>Today</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Logins */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Monthly Logins</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[140px]">
                  <MiniBarChart data={login.monthly_logins || []} dataKey="count" labelKey="month" color="#6366f1" height={120} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
                  {(login.monthly_logins || []).filter((_, i) => i % 3 === 0).map(m => (
                    <span key={m.month}>{m.month}</span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tasks Created Per Day */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" /> Tasks Created (Last 14 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[140px]">
                  <MiniBarChart data={u.tasks_per_day || []} color="#10b981" height={120} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
                  <span>{(u.tasks_per_day || [])[0]?.date?.slice(5) || ''}</span>
                  <span>Today</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Active Users + Users by Role */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Most Active Users (This Month)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(u.top_active_users || []).map((user, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        {i + 1}
                      </span>
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-[10px]">{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-sm font-medium truncate">{user.name}</span>
                      <Badge variant="secondary" className="text-xs">{user.logins} logins</Badge>
                    </div>
                  ))}
                  {(!u.top_active_users || u.top_active_users.length === 0) && (
                    <p className="text-gray-400 text-sm text-center py-4">No login data this month</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Users by Role</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(active.users_by_role || {}).map(([role, count]) => (
                    <ProgressBar key={role} label={role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} value={count} max={active.total_users || 1} color="bg-indigo-500" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── STORAGE TAB ── */}
        <TabsContent value="storage" className="mt-6 space-y-6">
          {(() => {
            const st = storageData || {};
            const summary = st.summary || {};
            const byModule = st.by_module || {};
            const byOffice = st.by_office || {};
            const fileUploads = st.file_uploads || {};

            const moduleEntries = Object.entries(byModule)
              .filter(([, v]) => v.documents > 0)
              .sort((a, b) => b[1].size_bytes - a[1].size_bytes);

            const officeEntries = Object.entries(byOffice)
              .sort((a, b) => b[1].total_bytes_estimated - a[1].total_bytes_estimated);

            const maxModuleBytes = moduleEntries.length > 0 ? moduleEntries[0][1].size_bytes : 1;

            return (
              <>
                {/* Storage Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Storage</p>
                          <p className="text-2xl font-bold mt-1">{summary.total_storage_display || '0 B'}</p>
                          <p className="text-xs text-gray-500 mt-1">{(summary.database_documents || 0).toLocaleString()} total documents</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-[#0F2E6E]/10 text-[#0F2E6E]"><HardDrive className="h-5 w-5" /></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Database</p>
                          <p className="text-2xl font-bold mt-1">{summary.database_display || '0 B'}</p>
                          <p className="text-xs text-gray-500 mt-1">All collections</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-blue-100 text-blue-600"><Database className="h-5 w-5" /></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">File Uploads</p>
                          <p className="text-2xl font-bold mt-1">{summary.file_uploads_display || '0 B'}</p>
                          <p className="text-xs text-gray-500 mt-1">Letters, Ping</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-green-100 text-green-600"><FolderOpen className="h-5 w-5" /></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Profile Pictures</p>
                          <p className="text-2xl font-bold mt-1">{summary.profile_pics_display || '0 B'}</p>
                          <p className="text-xs text-gray-500 mt-1">{byModule['Employees']?.profile_pics_count || 0} photos</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-purple-100 text-purple-600"><Image className="h-5 w-5" /></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Storage by Module */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Database className="h-4 w-4" /> Storage by Module
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {moduleEntries.map(([modName, info]) => {
                          const pct = maxModuleBytes > 0 ? Math.round((info.size_bytes / maxModuleBytes) * 100) : 0;
                          return (
                            <div key={modName} className="space-y-1" data-testid={`storage-module-${modName.toLowerCase().replace(/\s/g, '-')}`}>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-700 font-medium">{modName}</span>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <span>{info.documents.toLocaleString()} docs</span>
                                  <span className="font-semibold text-gray-700">{info.size_display}</span>
                                </div>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#0F2E6E] rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                              {modName === 'Employees' && info.profile_pics_count > 0 && (
                                <p className="text-[10px] text-purple-600 pl-1">
                                  + {info.profile_pics_display} in {info.profile_pics_count} profile pictures
                                </p>
                              )}
                            </div>
                          );
                        })}
                        {moduleEntries.length === 0 && (
                          <p className="text-gray-400 text-sm text-center py-4">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Storage by Office */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4" /> Storage by Office
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {officeEntries.map(([officeName, info]) => {
                          const modulesList = Object.entries(info.modules || {}).sort((a, b) => b[1].estimated_bytes - a[1].estimated_bytes);
                          return (
                            <div key={officeName} className="border rounded-lg p-4" data-testid={`storage-office-${info.office_id}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-[#0F2E6E]" />
                                  <span className="font-semibold text-sm">{officeName}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-bold">{info.total_display}</span>
                                  <p className="text-[10px] text-gray-500">{info.total_documents.toLocaleString()} docs</p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                {modulesList.map(([modName, mi]) => {
                                  const maxOff = info.total_bytes_estimated || 1;
                                  return (
                                    <div key={modName}>
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">{modName}</span>
                                        <span className="text-gray-700 font-medium">{mi.estimated_display} ({mi.documents})</span>
                                      </div>
                                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-0.5">
                                        <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(mi.estimated_bytes / maxOff) * 100}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {officeEntries.length === 0 && (
                          <p className="text-gray-400 text-sm text-center py-4">No offices</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* File Uploads Detail */}
                {Object.keys(fileUploads).length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FolderOpen className="h-4 w-4" /> File Upload Directories
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(fileUploads).map(([modName, info]) => (
                          <div key={modName} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                            <FolderOpen className="h-8 w-8 text-amber-500 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{modName} Attachments</p>
                              <p className="text-xs text-gray-500">{info.file_count} files</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">{info.size_display}</p>
                              <p className="text-[10px] text-gray-400 font-mono truncate max-w-[160px]">{info.path}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}
        </TabsContent>

        {/* ── WORKFORCE TAB ── */}
        <TabsContent value="workforce" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Gender Distribution */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Gender Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(demographics.gender_distribution || {}).map(([gender, count]) => (
                    <ProgressBar
                      key={gender}
                      label={`${gender} (${demographics.gender_percentage?.[gender] || 0}%)`}
                      value={count}
                      max={d.total_employees || 1}
                      color={gender === 'Male' ? 'bg-blue-500' : gender === 'Female' ? 'bg-pink-500' : 'bg-gray-400'}
                    />
                  ))}
                </div>
                {demographics.average_age && (
                  <div className="mt-4 pt-3 border-t text-sm text-gray-500">
                    Avg. Age: <span className="font-semibold text-gray-700">{demographics.average_age}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Age Distribution */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Age Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(demographics.age_distribution || {}).map(([range, count]) => (
                    <ProgressBar key={range} label={range} value={count} max={d.total_employees || 1} color="bg-teal-500" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Employment Types */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Employment Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(tenure.employment_type_breakdown || {}).map(([type, count]) => (
                    <ProgressBar key={type} label={type} value={count} max={d.total_employees || 1} color="bg-amber-500" />
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t text-sm text-gray-500 space-y-1">
                  <div>Avg. Tenure: <span className="font-semibold text-gray-700">{tenure.average_tenure_years || 0} yrs</span></div>
                  <div>New this year: <span className="font-semibold text-gray-700">{tenure.new_hires_this_year || 0}</span></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Departments + Job Titles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Departments ({depts.total_departments || 0})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(depts.department_breakdown || {}).slice(0, 10).map(([dept, count]) => (
                    <ProgressBar key={dept} label={dept} value={count} max={d.total_employees || 1} color="bg-[#0F2E6E]" />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><UserCheck className="h-4 w-4" /> Top Job Titles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(d.insights?.top_job_titles || {}).map(([title, count]) => (
                    <ProgressBar key={title} label={title} value={count} max={d.total_employees || 1} color="bg-violet-500" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── OPERATIONS TAB ── */}
        <TabsContent value="operations" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tasks */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" /> Tasks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-700">{tasks.total_tasks || 0}</p>
                    <p className="text-xs text-blue-600">Total</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-yellow-700">{tasks.pending_tasks || 0}</p>
                    <p className="text-xs text-yellow-600">Pending</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-red-700">{tasks.overdue_tasks || 0}</p>
                    <p className="text-xs text-red-600">Overdue</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-700">{tasks.completed_this_month || 0}</p>
                    <p className="text-xs text-green-600">Done (Month)</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">By Priority</p>
                  <div className="space-y-2">
                    {Object.entries(tasks.priority_breakdown || {}).map(([priority, count]) => (
                      <ProgressBar
                        key={priority}
                        label={priority}
                        value={count}
                        max={tasks.pending_tasks || 1}
                        color={priority === 'Urgent' ? 'bg-red-500' : priority === 'High' ? 'bg-orange-500' : priority === 'Medium' ? 'bg-blue-500' : 'bg-gray-400'}
                      />
                    ))}
                  </div>
                </div>
                <div className="pt-3 border-t text-sm">
                  Completion Rate: <span className="font-bold text-green-600">{tasks.completion_rate || 0}%</span>
                </div>
              </CardContent>
            </Card>

            {/* Attendance / Leave */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Attendance & Leave</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-700">{attendance.checked_in_today || 0}</p>
                    <p className="text-xs text-green-600">Checked In</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-700">{attendance.attendance_rate_today || 0}%</p>
                    <p className="text-xs text-blue-600">Rate Today</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-orange-700">{attendance.on_leave_today || 0}</p>
                    <p className="text-xs text-orange-600">On Leave</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-yellow-700">{attendance.pending_leave_requests || 0}</p>
                    <p className="text-xs text-yellow-600">Pending Leave</p>
                  </div>
                </div>
                {Object.keys(attendance.leave_type_breakdown || {}).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Leave Types (Approved)</p>
                    <div className="space-y-2">
                      {Object.entries(attendance.leave_type_breakdown || {}).map(([type, count]) => (
                        <ProgressBar key={type} label={type} value={count} max={d.total_employees || 1} color="bg-orange-400" />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" /> Expenses</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-yellow-700">{expenses.pending_approvals || 0}</p>
                    <p className="text-xs text-yellow-600">Pending</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-700">
                      {(expenses.total_this_month || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-green-600">This Month</p>
                  </div>
                </div>
                {(expenses.by_office || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Expenses by Office</p>
                    <div className="space-y-2">
                      {(expenses.by_office || []).map((o) => (
                        <div key={o.office_id} className="flex justify-between text-sm">
                          <span className="text-gray-600 truncate">{o.office_name}</span>
                          <span className="font-medium">{o.total?.toLocaleString()} ({o.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer timestamp */}
      <div className="text-center text-xs text-gray-400">
        Data generated at {d.generated_at?.slice(0, 19).replace('T', ' ')} UTC
      </div>
    </div>
  );
};

export default PlatformAnalytics;
